import React, { useCallback } from 'react';
import { route } from '../../../../../services/utils';
import {
  LIVESTOCK_ANIMALS_AND_EVENTS_INDEX,
  LIVESTOCK_ANIMALS_OTHER_VIEW,
} from '../../../../../router/routes';
import useFormBody from './use-form-body';
import { useOtherState } from './use-other-state';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOtherUpdate } from './use-other-update';
import Form from '../../../../../components/form';
import { useEditFooter } from '../use-edit-footer';
import useFormTop from '../use-form-top';
import AnimalsAndEventsTabsKeys from '../../enums/animals-and-events-tab-keys';
import animalEventFormCss from '../animal-events-form.module.css';

import * as yup from 'yup';
import FormTable from './form-table';
import AnimalType from '../../../../../enums/animal-type';
import { useQuery } from '../../../../../hooks/use-query';

export const Option = yup.object().shape({
  value: yup.string().required(),
});

export const OptionNulable = yup.object().shape({
  value: yup.string().nullable(),
});

const warehouseSchema = yup.object().shape({
  animal_event_product_id: yup.string().nullable(),
  barn_id: Option,
  product_amount_for_one: yup.object().shape({
    amount: yup.string().nullable(),
    unit: OptionNulable,
  }),
  product_amount_total: yup.string().required(),
  product_unit: Option,
});

const productSchema = yup.object().shape({
  product_category_id: Option,
  product_type_id: Option,
  product_id: Option,
  barns: yup.array().of(warehouseSchema),
});

export default function EditOther() {
  const { farmId } = useParams<{
    farmId: string;
  }>();

  const { t } = useTranslation();

  const transKeyPrefix = 'pages.livestock.animals_and_events.';
  const tp = useCallback(
    (key: string, params?: { [key: string]: string | number }) =>
      t(transKeyPrefix.concat(key), params),
    [t]
  );

  const schema = yup.object().shape({
    species: OptionNulable,
    barn_ldc_nr: OptionNulable,
    animal_id: OptionNulable,
    status: yup.string().required(),
    period: yup.boolean(),
    date: yup.mixed().when('period', {
      is: false,
      then: yup.date().required(),
      otherwise: yup.mixed(),
    }),

    date_from: yup.mixed().when('period', {
      is: true,
      then: yup.date().required('events.date_required'),
      otherwise: yup.mixed(),
    }),
    date_to: yup.mixed().when('period', {
      is: true,
      then: yup
        .date()
        .required()
        .test('is-greater', tp('events.dates_required'), function (value) {
          const { date_from } = this.parent;
          return value > date_from;
        }),
      otherwise: yup.mixed(),
    }),
    event_type: yup.string().required(),
    product_required: yup.boolean(),
    products: yup.mixed().when('product_required', {
      is: true,
      then: yup.array().of(productSchema),
      otherwise: yup.mixed(),
    }),
    notes: yup.string().max(4000).nullable(),
    costs: yup.string(),
    file: yup.mixed(),
  });
  const [queries] = useQuery();
  const footer = useEditFooter(
    `${LIVESTOCK_ANIMALS_OTHER_VIEW}${
      queries.to ? '?to='.concat(queries.to) : ''
    }`
  );
  const [otherState, animals, removeRow, addRow] = useOtherState();
  const body = useFormBody(animals);
  const top = useFormTop({ addRow });
  const submit = useOtherUpdate(animals);

  return (
    <Form
      backTo={route(LIVESTOCK_ANIMALS_AND_EVENTS_INDEX, {
        tab:
          queries.to === 'events'
            ? AnimalsAndEventsTabsKeys.events
            : AnimalsAndEventsTabsKeys.animals,
        farmId,
      })}
      schema={schema}
      title={tp('animals.types.other')}
      body={body}
      top={top}
      footer={footer}
      mode={'edit'}
      submit={submit}
      values={otherState}
      className={animalEventFormCss['animal_event_form']}
    >
      <FormTable
        eventType={AnimalType.other}
        removeRow={removeRow}
        animals={animals}
        showRemove={true}
      />
    </Form>
  );
}
